import { Helmet } from 'react-helmet';
import React, { useState, useEffect } from 'react';
import { Alert, Modal, Button ,Spinner} from 'react-bootstrap';
import ProjectList from '../../components/admin/ProjectList';
import ProjectEdit from '../../components/admin/ProjectEdit';
import { getAdminProjects, updateProject, deleteProject } from './../../api';

function AdminProject() {
  // eslint-disable-next-line
  const title = 'List of All Projects';
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [alert, setAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setIsLoading(true);  // Start loading
      const response = await getAdminProjects();
      setProjects(response.data);
    } catch (error) {
      setAlert({ variant: 'danger', message: 'Failed to fetch Projects' });
    } finally {
      setIsLoading(false);  // End loading regardless of success/failure
    }
  };

  const handleEditProject = (Project) => {
    setSelectedProject(Project);
    setShowModal(true);
  };


  const handleDeleteShow = (project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedProject(null);
  };

  const handleDeleteProject = async () => {
    try {
      await deleteProject(selectedProject.id);
      fetchProjects();
      handleDeleteClose();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleUpdateProject = async (updatedProject) => {
    try {
      await updateProject(updatedProject.id, updatedProject);
      fetchProjects();
      setShowModal(false);
      setSelectedProject(null);
      setAlert({ variant: 'success', message: 'Project updated successfully' });
    } catch (error) {
      setAlert({ variant: 'danger', message: 'Failed to update Project' });
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedProjects = React.useMemo(() => {
    let sortableProjects = [...projects];
    if (sortConfig.key !== null) {
      sortableProjects.sort((a, b) => {
        if (sortConfig.key === 'is_active') {
          // Handle cases where is_active might not exist
          const aValue = a.is_active === undefined ? false : a.is_active;
          const bValue = b.is_active === undefined ? false : b.is_active;
          return sortConfig.direction === 'ascending'
            ? (aValue === bValue ? 0 : aValue ? -1 : 1)
            : (aValue === bValue ? 0 : aValue ? 1 : -1);
        } else {
          if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
        }
        return 0;
      });
    }
    return sortableProjects;
  }, [projects, sortConfig]);

  const filteredProjects = sortedProjects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.is_active !== undefined && p.is_active.toString().includes(searchTerm.toLowerCase()))
  );

  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="container-fluid mb-5 px-3">
        <div className="row g-5 pt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-header pt-3 bg-white">

                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h4 className="text-secondary">Projects Directory</h4>
                  </div>

                </div>
              </div>
              <div className="table-responsive position-relative">
                {isLoading ? (
                  <div className="text-center mt-5">
                    <Spinner
                      animation="border"
                      variant="primary"
                      role="status"
                      style={{ width: "3rem", height: "3rem" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-2">Loading user data...</p>
                  </div>
                ) : (
                  <ProjectList
                    projects={currentProjects}
                    onEditProject={handleEditProject}
                    onDeleteProject={handleDeleteShow}
                    onSearch={handleSearch}
                    onSort={handleSort}
                    sortConfig={sortConfig}
                    currentPage={currentPage}
                    projectsPerPage={projectsPerPage}
                    totalProjects={filteredProjects.length}
                    paginate={paginate}

                  />
                )}
              </div>
            </div>
          </div>
          <div>
            {alert && (
              <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
                {alert.message}
              </Alert>
            )}
          </div>
        </div >
      </div >



      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProject && (
            <ProjectEdit
              project={selectedProject}
              onUpdateProject={handleUpdateProject}
              onCancel={() => setShowModal(false)}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the project "{selectedProject?.name}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteProject}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

    </>
  );
}

export default AdminProject;